import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Sidebar from './Sidebar';
import { useParams, useLocation } from 'react-router-dom';
import { Session } from "./utils/session";
import '../styles.css';
import { API_BASE_URL } from '../config';

const authHeaders = () => {
  const token = Session.getToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};

function formatFullDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export default function ViewActivityHistory() {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const { id } = useParams();
  const { state } = useLocation();
  const incomingWorkDate = state?.workDate || null;

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workDate, setWorkDate] = useState(incomingWorkDate);
  const [modalImage, setModalImage] = useState(null);
  const [imageError, setImageError] = useState(false);

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    } else {
      setHideDesktop((s) => !s);
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) overlay.style.display = showMobileSidebar ? 'block' : 'none';

    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      sidebar.classList.toggle('show-mobile', showMobileSidebar);
      sidebar.classList.toggle('hide-desktop', hideDesktop);
    }
    const content = document.getElementById('content');
    if (content) content.classList.toggle('full-width', hideDesktop);
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
  fetchActivityHistory();
}, []);
const fetchActivityHistory = useCallback(async () => {
  setLoading(true);
  setError(null);

  try {
    const dateObj = workDate ? new Date(workDate) : new Date();
    const dateStr = dateObj.toISOString().slice(0, 10);

    const API_URL = `${API_BASE_URL}/api/ViewActivityHistory/ViewActivityHistoryByUserId?userId=${encodeURIComponent(id)}&date=${encodeURIComponent(dateStr)}`;

    const res = await fetch(API_URL, { headers: authHeaders() }); // Use authHeaders

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      if (res.status === 404) {
        console.warn('Activity history not found for', id, dateStr, txt);
        setEntries([]);
        return;
      }
      throw new Error(`API error ${res.status}: ${txt}`);
    }

    const data = await res.json().catch(() => null);

    if (data) {
      if (Array.isArray(data)) {
        setEntries(data);
      } else if (Array.isArray(data.data)) {
        setEntries(data.data);
      } else if (Array.isArray(data.result)) {
        setEntries(data.result);
      } else if (Array.isArray(data.items)) {
        setEntries(data.items);
      } else {
        setEntries([]);
      }
    } else {
      setEntries([]);
    }
  } catch (err) {
    console.error(err);
    setError(err.message || 'Failed to load activity history.');
    setEntries([]);
  } finally {
    setLoading(false);
  }
}, [id, workDate]);


 const mappedEntries = useMemo(() => {
  const mapped = (entries || []).map((dto) => {
    const snaps = Array.isArray(dto.snaps) ? dto.snaps : [];

    const thumbs = snaps.map((s) => {
      const snapDate = s.snapOn ? new Date(s.snapOn) : null;

      const timeLabel =
        snapDate && !isNaN(snapDate)
          ? snapDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            })
          : s.snapOnTime || '';

      // ✅ API se direct totalClicks aa raha hai
      const totalClicks = s.totalClicks ?? 0;

      let pct = 0;
      let status = 'IDLE';

      if (totalClicks >= 100) {
        pct = 100;
        status = 'ACTIVE';
      } else if (totalClicks < 5) {
        pct = 0;
        status = 'IDLE';
      } else {
        pct = Math.round((totalClicks / 100) * 100);
        status = 'LOW';
      }

      return {
        src: "/images/tracing.Jpg",
        time: timeLabel,
        pct,
        clicks: totalClicks,
        status,          // ACTIVE / LOW / IDLE
        id: s.userSnapId,
      };
    });

    return {
      range: dto.timeRange || '',
      title: dto.machineName || 'Activity',
      thumbs,
    };
  });

  // ⏱ total time from timeRange (same as before)
  let summedMinutes = 0;
  mapped.forEach((e) => {
    const m = (e.range || '').match(/\((\d+):(\d{2})\s*hrs?\)/);
    if (m) summedMinutes += +m[1] * 60 + +m[2];
  });

  const hours = Math.floor(summedMinutes / 60);
  const mins = summedMinutes % 60;

  return {
    mapped,
    totalTimeStr: `${hours}:${mins.toString().padStart(2, '0')}`,
  };
}, [entries]);

  useEffect(() => {
    const contentEl = document.getElementById('content');
    if (contentEl && !contentEl.style.position) contentEl.style.position = 'relative';
  }, []);

  const openImage = useCallback((index, images) => {
    setModalImage({ index, images });
    setImageError(false);
    document.body.style.overflow = 'hidden';
  }, []);
  const closeModal = useCallback(() => {
    setModalImage(null);
    setImageError(false);
    document.body.style.overflow = '';
  }, []);

  const prevImage = useCallback(() => {
    if (!modalImage) return;
    setModalImage((prev) => ({
      ...prev,
      index: (prev.index - 1 + prev.images.length) % prev.images.length
    }));
    setImageError(false);
  }, [modalImage]);

  const nextImage = useCallback(() => {
    if (!modalImage) return;
    setModalImage((prev) => ({
      ...prev,
      index: (prev.index + 1) % prev.images.length
    }));
    setImageError(false);
  }, [modalImage]);

  useEffect(() => {
    const onKey = (e) => {
      if (!modalImage) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalImage, closeModal, prevImage, nextImage]);

  const summaryStyle = {
    background: '#fff',
    borderRadius: 8,
    padding: '18px 20px',
    boxShadow: 'none',
    border: '1px solid #eef1f4'
  };

  const thumbContainer = {
    borderRadius: 6,
    overflow: 'hidden',
    background: '#f8f9fa',
    border: '1px solid #eef1f4'
  };

  return (
    <div>
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar"><Sidebar /></div>
      <div id="content">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <button type="button" id="sidebarCollapse" className="btn btn-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
              <i className="fas fa-bars" />
            </button>
            <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
            <div><h5 className="mb-0">{formatFullDate(workDate)}</h5></div>
          </div>
        </div>

        {/* Summary card */}
        <div style={summaryStyle} className="mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
            <div>
              <div className="d-flex align-items-center gap-3">
                 <div style={{ fontSize: 28, fontWeight: 600 }}>Total: {mappedEntries.totalTimeStr}</div>
                  <button type="button" className="btn btn-link p-0" title={loading ? 'Refreshing...' : 'Refresh total'}
                  onClick={fetchActivityHistory}disabled={loading}                  >
                  <i className={`fas fa-sync-alt text-muted ${loading ? 'fa-spin' : ''}`} /></button>
              </div>
              <div className="text-muted small mt-1">Detailed tracked time for the selected day</div>
            </div>
            <div className="d-flex align-items-center gap-4 mt-3 mt-md-0">
              <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#28a745', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Tracked</div>
                </div>
                <div className="fw-bold">{mappedEntries.totalTimeStr}</div>
              </div>
              <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#0dcaf0', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Manual</div>
                </div>
                <div className="fw-bold">—</div>
              </div>
              <div className="text-center">
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <span style={{ width: 10, height: 10, background: '#dc3545', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">Overtime</div>
                </div>
                <div className="fw-bold">—</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          {mappedEntries.mapped.map((entry, ei) => (
            <div key={ei} className="mb-5">
              <div className="d-flex align-items-start justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ width: 12, height: 12, background: '#28a745', borderRadius: 12, display: 'inline-block' }} />
                  <div className="small text-muted">{entry.range}</div>
                </div>
                <div>
                  <button className="btn btn-sm btn-light rounded-circle"><i className="fas fa-ellipsis-h text-muted" /></button>
                </div>
              </div>
              <div className="mb-3"><div className="fw-semibold">{entry.title}</div></div>
              <div className="mb-3">
                <div className="d-md-none">
                  <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 }}>
                    {entry.thumbs.map((t, ti) => (
                      <div key={ti} style={{ minWidth: 160, maxWidth: 200 }}>
                        <div style={thumbContainer}>
                          <img src={t.src} alt={t.time} style={{ width: '100%', height: 90, objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => openImage(ti, entry.thumbs.map((th) => th.src))} />
                        </div>
                        <div className="mt-2" style={{ height: 8, background: '#e9ecef', borderRadius: 4 }}>
                          <div style={{ height: '100%', width: `${t.pct}%`, background: '#28a745', borderRadius: 4 }} />
                        </div>
                        <div className="small text-center text-muted mt-2">{t.time}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="row gx-3 gy-3 d-none d-md-flex align-items-end">
                  {entry.thumbs.map((t, ti) => (
                    <div key={ti} className="col-auto" style={{ width: 160 }}>
                      <div style={thumbContainer}>
                        <img src={t.src} alt={t.time} style={{ width: '100%', height: 90, objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => openImage(ti, entry.thumbs.map((th) => th.src))} />
                      </div>
                      <div className="mt-2" style={{ height: 8, background: '#e9ecef', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: `${t.pct}%`, background: '#28a745', borderRadius: 4 }} />
                      </div>
                      <div className="small text-center text-muted mt-2">{t.time}</div>
                    </div>
                  ))}
                </div>
              </div>
              <hr />
            </div>
          ))}
        </div>
      </div>
     {modalImage && (
  <div
    role="dialog"
    aria-modal="true"
    onClick={closeModal}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      overflow: 'auto',
      padding: 16
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'relative',
        maxWidth: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <button
        onClick={closeModal}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 5,
          background: '#0084fe',
          color: '#fff',
          border: 'none',
          padding: '5px 10px',
          borderRadius: 6,
          cursor: 'pointer'
        }}
      >
        ✕
      </button>
      {modalImage.index > 0 && (
        <button
          onClick={prevImage}
          style={{
            position: 'absolute',
            top: '50%',
            left: 8,
            transform: 'translateY(-50%)',
            zIndex: 5,
            background: '#0084fe',
            color: '#fff',
            border: 'none',
            padding: '5px 10px',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          ‹
        </button>
      )}
      {modalImage.index < modalImage.images.length - 1 && (
        <button
          onClick={nextImage}
          style={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            zIndex: 5,
            background: '#0084fe',
            color: '#fff',
            border: 'none',
            padding: '5px 10px',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          ›
        </button>
      )}
      {modalImage.images[modalImage.index] && !imageError ? (
        <img
          src={modalImage.images[modalImage.index]}
          alt={`Preview ${modalImage.index + 1}`}
          onError={() => setImageError(true)}
          style={{
            width: 'auto',
            height: 'auto',
            maxWidth: '100%',
            maxHeight: '95vh',
            objectFit: 'contain',
          }}
        />
      ) : (
        <div
          style={{
            width: '82.4vw',
            height: '95vh',
            background: 'rgb(215 205 205)',
            color: '#343a40',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 16,
            borderRadius: 6
          }}
        >
          No Image Available.
        </div>
      )}

      {modalImage.caption && (
        <div style={{ color: '#fff', marginTop: 8, textAlign: 'center', fontSize: 14 }}>
          {modalImage.caption}
        </div>
      )}
    </div>
  </div>
)}

    </div>
  );
}
